import { authService } from '@/lib/auth'
import { env } from '@/config/env'
import type { PortalSessionResponse, UserUsageData } from './types'

export type { UserUsageData }

export const BASE_URL = env.apiEndpoint // lambda function url

// Custom error class for rate limit (429) errors
export class RateLimitError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'RateLimitError'
  }
}

// Helper to check if a message indicates a rate limit error
const isRateLimitMessage = (message: string): boolean => {
  const lowerMessage = message.toLowerCase()
  return (
    lowerMessage.includes('limit exceeded') ||
    lowerMessage.includes('usage limit')
  )
}

export const useMock = env.isDev

export const EventSourceMessageTypes = {
  STATUS: 'status',
  COMPLETE: 'complete',
  FRAME: 'frame',
}

export enum ConversationStep {
  INITIAL_ANALYSIS = 'initial_analysis',
  GOALS_COLLECTED = 'goals_collected',
  ENHANCEMENTS_SUGGESTED = 'enhancements_suggested',
  IMAGE_EDITED = 'image_edited',
}

export enum ResponseType {
  EXPLANATION_WITH_QUESTION = 'explanation_with_question',
  ENHANCEMENT_SUGGESTIONS = 'enhancement_suggestions',
  EDITED_IMAGE = 'edited_image',
}

export interface ConversationResult {
  run_id: string
  response: string
  timestamp?: string
  conversation_step?: ConversationStep
  response_type?: ResponseType
  conversation_id?: string
}

// Helper function to create authenticated headers
const getAuthHeaders = async (): Promise<HeadersInit> => {
  const token = await authService.getAccessToken()
  const headers: HeadersInit = {}

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  return headers
}

// Authenticated fetch wrapper
const authenticatedFetch = async (
  url: string,
  options: RequestInit = {},
): Promise<Response> => {
  const authHeaders = await getAuthHeaders()

  const mergedOptions: RequestInit = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...(options.headers || {}),
    },
  }

  const response = await fetch(url, mergedOptions)

  // Handle 401 responses
  if (response.status === 401) {
    throw new Error('Authentication failed - please sign in again')
  }

  // Handle 429 rate limit responses or responses with rate limit messages
  if (response.status === 429 || !response.ok) {
    const defaultMessage =
      'You have reached your usage limit. Please upgrade your plan to continue.'

    // Clone response so we can read the body and still return the response if not a rate limit error
    const clonedResponse = response.clone()

    try {
      const data = await clonedResponse.json()
      const errorMessage = data.message || data.error || ''

      // Check if it's a 429 or if the message contains rate limit keywords
      if (response.status === 429 || isRateLimitMessage(errorMessage)) {
        throw new RateLimitError(errorMessage || defaultMessage)
      }
    } catch (e) {
      // If it's already a RateLimitError, rethrow it
      if (e instanceof RateLimitError) {
        throw e
      }
      // For 429 with unparseable body, still throw rate limit error
      if (response.status === 429) {
        throw new RateLimitError(defaultMessage)
      }
    }
  }

  return response
}

// Ping endpoint (no authentication required - used for both warmup and health checks)
export const ping = async () => {
  const response = await fetch(`${BASE_URL}/ping`)
  if (!response.ok) {
    throw new Error('Failed to ping')
  }
  const data = await response.json()
  if (data.body) {
    return JSON.parse(data.body)
  }
  return data
}

export const getS3UploadUrl = async (fileName: string, mimeType: string) => {
  try {
    const response = await authenticatedFetch(`${BASE_URL}/upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        file_name: fileName,
        file_type: mimeType,
      }),
    })

    if (!response.ok) {
      throw new Error(
        `Failed to upload file: ${response.status} ${response.statusText}`,
      )
    }

    const data = await response.json()
    if (data.body) {
      return JSON.parse(data.body)
    }
    return data
  } catch (e) {
    console.error(e)
    throw new Error('Failed to upload file')
  }
}

// Generic streaming function that handles SSE wrapped in JSON with authentication
const processGenericStream = async (
  streamUrl: string,
  onMessage: (data: any) => boolean | void,
  onError: (error?: Error) => void,
) => {
  console.log('Connecting to authenticated stream:', streamUrl)

  let isConnectionClosed = false

  try {
    const response = await authenticatedFetch(streamUrl, {
      method: 'GET',
      headers: {
        Accept: 'text/plain',
        'Cache-Control': 'no-cache',
      },
    })

    if (!response.ok) {
      throw new Error(
        `HTTP error! status: ${response.status} ${response.statusText}`,
      )
    }

    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('Failed to get stream reader')
    }

    const decoder = new TextDecoder()
    let accumulatedBuffer = ''

    const processStream = async () => {
      try {
        while (!isConnectionClosed) {
          const { done, value } = await reader.read()

          if (done) {
            console.log('Stream completed')
            // Process the complete accumulated buffer
            if (accumulatedBuffer.trim()) {
              try {
                processSSEBody(accumulatedBuffer)
              } catch (jsonError) {
                console.error(
                  'Error parsing JSON response:',
                  jsonError,
                  'Buffer:',
                  accumulatedBuffer,
                )
              }
            }
            break
          }

          const chunk = decoder.decode(value, { stream: true })
          accumulatedBuffer += chunk

          // Just accumulate - parse only when stream is complete
        }
      } catch (streamError) {
        console.error('Stream error:', streamError)
        if (!isConnectionClosed) {
          isConnectionClosed = true
          onError(streamError instanceof Error ? streamError : undefined)
        }
      } finally {
        reader.releaseLock()
      }
    }

    const processSSEBody = (sseBody: string) => {
      // Parse the SSE format from the response body
      if (sseBody && sseBody.includes('data: ')) {
        const lines = sseBody.split('\n')

        for (const line of lines) {
          if (line.trim() === '' || isConnectionClosed) continue

          // Handle SSE format: look for data: lines
          if (line.startsWith('data: ')) {
            try {
              const jsonData = line.substring(6) // Remove 'data: ' prefix
              const data = JSON.parse(jsonData)
              console.log('Received stream message:', data)

              // Let the caller handle the message
              const shouldClose = onMessage(data)
              if (shouldClose) {
                isConnectionClosed = true
                return
              }
            } catch (parseError) {
              console.error(
                'Error parsing stream data:',
                parseError,
                'Line:',
                line,
              )
            }
          }
          // Skip event: lines and other non-data lines
        }
      }
    }

    processStream()
  } catch (fetchError) {
    console.error('Fetch error:', fetchError)
    if (!isConnectionClosed) {
      isConnectionClosed = true
      onError(fetchError instanceof Error ? fetchError : undefined)
    }
  }

  return () => {
    if (!isConnectionClosed) {
      isConnectionClosed = true
    }
  }
}

export const getAnalysisRuns = async () => {
  const response = await authenticatedFetch(`${BASE_URL}/runs`)
  if (!response.ok) {
    throw new Error(
      `Failed to get analysis runs: ${response.status} ${response.statusText}`,
    )
  }
  return response.json()
}

export const getAnalysisRun = async (runId: string) => {
  const response = await authenticatedFetch(`${BASE_URL}/runs/${runId}`)
  if (!response.ok) {
    throw new Error(
      `Failed to get analysis run: ${response.status} ${response.statusText}`,
    )
  }
  return response.json()
}

export const updateAnalysisRun = async (runId: string, data: any) => {
  const response = await authenticatedFetch(`${BASE_URL}/runs/${runId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
  return response.json()
}

export const processImageStream = async (
  s3Uri: string,
  onStatus: (progress: number, message: string) => void,
  onComplete: (prediction: any) => void,
  onError: (error?: Error) => void,
) => {
  const streamUrl = `${BASE_URL}/predict_image/stream?s3_uri=${encodeURIComponent(s3Uri)}`

  return processGenericStream(
    streamUrl,
    data => {
      switch (data.type) {
        case EventSourceMessageTypes.STATUS:
          onStatus(data.progress, data.message)
          break
        case EventSourceMessageTypes.COMPLETE:
          onComplete(data.prediction)
          return true // Signal to close connection
        default:
          console.log(data)
          break
      }
      return false // Continue processing
    },
    onError,
  )
}

export const processVideoStream = async (
  s3Uri: string,
  onStatus: (progress: number, message: string) => void,
  onFrame: (prediction: any, frameIndex: number) => void,
  onComplete: () => void,
  onError: (error?: Error) => void,
) => {
  const streamUrl = `${BASE_URL}/predict_video/stream?s3_uri=${encodeURIComponent(s3Uri)}`

  return processGenericStream(
    streamUrl,
    data => {
      switch (data.type) {
        case EventSourceMessageTypes.STATUS:
          onStatus(data.progress ?? 0, data.message)
          break
        case EventSourceMessageTypes.FRAME:
          onFrame(data.prediction, data.frame_index)
          onStatus(
            data.progress ?? 0,
            `Processing ${data.frame_index + 1} of ${data.total_frames} frames`,
          )
          break
        case EventSourceMessageTypes.COMPLETE:
          onComplete()
          return true // Signal to close connection
        default:
          console.log(data)
          break
      }
      return false // Continue processing
    },
    onError,
  )
}

export const getImageAnalysisExplanations = async (
  runId: string,
): Promise<ConversationResult> => {
  const response = await authenticatedFetch(
    `${BASE_URL}/runs/${runId}/multi-turn/start`,
    {
      method: 'POST',
    },
  )
  if (!response.ok) {
    throw new Error(
      `Failed to get image analysis explanations: ${response.status} ${response.statusText}`,
    )
  }
  return response.json()
}

export const getImageEnhancementSuggestions = async (
  runId: string,
  conversationId: string,
  userResponse: string,
): Promise<ConversationResult> => {
  const response = await authenticatedFetch(
    `${BASE_URL}/runs/${runId}/multi-turn/continue`,
    {
      method: 'POST',
      body: JSON.stringify({
        run_id: runId,
        conversation_id: conversationId,
        user_response: userResponse,
      }),
    },
  )
  if (!response.ok) {
    throw new Error(
      `Failed to get image enhancement suggestions: ${response.status} ${response.statusText}`,
    )
  }
  return response.json()
}

export const generateEnhancedImage = async (
  runId: string,
  conversationId: string,
  imageUri: string,
) => {
  const response = await authenticatedFetch(
    `${BASE_URL}/runs/${runId}/multi-turn/edit-image`,
    {
      method: 'POST',
      body: JSON.stringify({
        run_id: runId,
        conversation_id: conversationId,
        image_url: imageUri,
      }),
    },
  )
  if (!response.ok) {
    throw new Error(
      `Failed to generate enhanced image: ${response.status} ${response.statusText}`,
    )
  }
  return response.json()
}

export const getUserUsage = async (): Promise<UserUsageData> => {
  const response = await authenticatedFetch(`${BASE_URL}/usage`)
  if (!response.ok) {
    throw new Error(
      `Failed to get user usage: ${response.status} ${response.statusText}`,
    )
  }
  return response.json()
}

export const getStripeCustomerPortalUrl = async (): Promise<PortalSessionResponse> => {
  const response = await authenticatedFetch(
    `${BASE_URL}/create-portal-session?frontend_url=${window.location.origin}`,
  )
  if (!response.ok) {
    throw new Error(
      `Failed to get stripe customer portal url: ${response.status} ${response.statusText}`,
    )
  }
  return response.json()
}
